/**
 * QuickSearch provides a modal-based, debounced, asynchronous search UI.
 * It handles modal display, manages fetch requests, and updates results dynamically.
 * 
 * Usage:
 *   const quickSearch = new QuickSearch("/search/url");
 *   quickSearch.registerInput(document.querySelector('#search-input'));
 *   
 *   <input data-state="new" type="text" onkeydown="window.quickSearch.onSearch(this)"/>
 */
class QuickSearch {

    // Instance variables
    _timeout;          // Used for debouncing the search
    _url;              // Base URL for search API
    _abortController;  // Used to cancel in-progress fetches
    _$modal;           // Reference to the currently open modal

    /**
     * @param {string} url - The search API endpoint.
     */
    constructor(url) {
        this._url = url;
    }

    /**
     * Opens the search modal and sets up lifecycle events.
     * @returns {jQuery} The modal element.
     */
    open() {
        // Create a new AbortController for each modal open
        this._abortController = new AbortController();

        // Open the modal (assumed openModal is globally available)
        this._$modal = openModal('/System/QuickSearch/Find', () => {
            // On modal close
            this._$modal.attr('aria-hidden', true);
            if (!this._abortController)
                return;
            // Abort any pending fetch
            this._abortController.abort('modal closed');
            this._abortController = null;
        });

        // When modal is shown, call onLoad
        this._$modal.one('shown.bs.modal', (event) => {
            this._$modal.attr('aria-hidden', false);
            this.onLoad(event);
        });
        return this._$modal;
    }

    /**
     * Handler for when the modal loads.
     * @param {Event} event - Modal show event.
     */
    onLoad(event) {
        this.setState(event.currentTarget, 'new');
    }

    /**
     * Sets the UI state of the modal.
     * @param {HTMLElement} htmlNode - Node inside the modal.
     * @param {string} state - 'new', 'loading', or 'result'.
     */
    setState(htmlNode, state) {
        const modal = htmlNode.closest('#modal');
        switch (state) {
            case "new": {
                // Focus input, hide loading, hide results
                const input = modal.querySelector('input[data-state="new"]');
                if (input)
                    input.focus();

                const loading = modal.querySelectorAll('[data-state="loading"]');
                if (loading)
                    loading.forEach(node => {
                        node.style.visibility = 'hidden';
                    });

                const result = modal.querySelectorAll('[data-state="result"]');
                if (result)
                    result.forEach(node => {
                        node.style.display = 'none';
                    });

                break;
            }
            case "loading": {
                // Show loading spinner/indicator
                modal.querySelectorAll('[data-state="loading"]').forEach(node => {
                    node.style.visibility = 'visible';
                });
                // Optionally hide results here if desired
                break;
            }
            case "result": {
                // Hide loading, show results
                const loaders = modal.querySelectorAll('[data-state="loading"]');
                if (loaders) 
                    loaders.forEach(node => {
                        node.style.visibility = 'hidden';
                    });
                
                modal.querySelectorAll('[data-state="result"]').forEach(node => {
                    node.style.display = 'block';
                });
                break;
            }
        }
    }

    /**
     * Executes the search query, updates modal content, and manages states.
     * Uses debounce to delay the request.
     * @param {HTMLInputElement} htmlNode - The input element that triggered search.
     */
    onSearch(htmlNode) {
        this.setState(htmlNode, 'loading');

        // Debounce: clear previous timeout if new keystroke occurs
        if (this._timeout) {
            clearTimeout(this._timeout);
        }
        this._timeout = setTimeout(async () => {
            let url = this._url;

            // Add '?' if not already present
            if (!this._url.includes('?'))
                url += "?";

            // Append search value as a query string
            url += new URLSearchParams({
                value: htmlNode.value
            }).toString();

            let result;
            try {
                // Fetch search results
                result = await fetch(url, {
                    method: 'GET',
                    signal: this._abortController.signal, // Supports abort
                    headers: {
                        'accept': 'application/json'
                    }
                });

                let pillars = await result.json(); // Results expected in grouped "pillars"

                // Clear tab content/results
                this._$modal.find('.tab-content .tab-pane').empty();

                // Reset all labels
                const $labels = this._$modal.find('ul.nav-tabs a label');
                $labels.text(0);
                $labels.toggleClass('label-success', false);
                $labels.toggleClass('label-warning', true);

                // Populate results for each pillar (group/category)
                for (let i = 0; i < pillars.length; i++) {
                    const pillar = pillars[i];
                    const $tab = this._$modal.find('#' + pillar.pillarIdentifier);
                    $tab.empty();

                    // Set label count for this tab
                    const $label = this._$modal.find('ul.nav-tabs a[href="#' + pillar.pillarIdentifier + '"] label');
                    $label.text(pillar.count > pillar.items.length ? (pillar.items.length + '+') : pillar.items.length);

                    if (pillar.count > 0) {
                        $label.toggleClass('label-success', true);
                        $label.toggleClass('label-warning', false);
                    }

                    // Render results as an unordered list
                    const unsortedList = document.createElement('ul');
                    $tab.append(unsortedList);

                    for (let y = 0; y < pillar.items.length; y++) {
                        const item = pillar.items[y];
                        const listItem = document.createElement('li');
                        unsortedList.appendChild(listItem);

                        const link = document.createElement('a');
                        link.target = '_blank';
                        link.innerText = item.description;
                        link.href = item.urlAction;

                        listItem.appendChild(link);
                    }
                }
            } 
            catch (error) {
                // Log and exit on fetch error or abort
                console.log(error);
                return;
            }

            // Show results
            this.setState(htmlNode, 'result');
        }, 800); // Debounce delay (ms)
    }

    /**
     * Attaches an event handler to open the QuickSearch modal.
     * @param {HTMLElement} htmlNode - The input/button to activate the search modal.
     */
    registerInput(htmlNode) {
        htmlNode.addEventListener('click', () => {
            this.open();
        });
    }
}

// Create a global instance, set up search input
const quickSearch = new QuickSearch("/System/QuickSearch/Query");
window.quickSearch = quickSearch;

// The window.quickSearch object is now available globally

// Register search modal trigger on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    quickSearch.registerInput(document.querySelector('#quick-search'));
});
